// Per-dialect identity / auto-increment synthesizer (v2 Slice A2).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice A2.
//
// Each engine spells the identity clause differently:
//   - mssql:   IDENTITY[(start, increment)]              — both or neither.
//   - pgsql:   GENERATED { ALWAYS | BY DEFAULT } AS IDENTITY [( opts )]
//   - mysql:   AUTO_INCREMENT                            — start/increment are
//              table-level (slice E3) and ignored on the column-level emit.
//   - oracle:  GENERATED { ALWAYS | BY DEFAULT [ON NULL] } AS IDENTITY [( opts )]
//   - sqlite:  AUTOINCREMENT                             — caller must place
//              `PRIMARY KEY` in `typeRef` (e.g. 'INTEGER PRIMARY KEY').
//
// `mode: 'by-default-on-null'` is oracle-only — every other engine throws.

import type { SqlEngine } from '../../../sql-grammar/model';
import type { IdentitySpec } from '../../types';
import { DdlEditError } from '../../types';

export function formatIdentitySpec(engine: SqlEngine, identity: IdentitySpec): string {
  if (engine === 'mssql') return formatMssql(identity);
  if (engine === 'postgres') return formatPgsql(identity);
  if (engine === 'mysql') return formatMysql();
  if (engine === 'oracle') return formatOracle(identity);
  return formatSqlite(identity);
}

function formatMssql(identity: IdentitySpec): string {
  if (identity.mode === 'by-default-on-null') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: mssql IDENTITY does not support mode 'by-default-on-null' `
        + `— that's an Oracle-only generation mode`,
    );
  }
  const startSet = identity.start !== undefined;
  const incrSet = identity.increment !== undefined;
  if (startSet !== incrSet) {
    throw new Error(
      `applyDdlEdit: mssql IDENTITY(start, increment) requires both 'start' `
        + `and 'increment', or neither — got only one`,
    );
  }
  if (identity.cycle !== undefined) {
    throw new Error(
      `applyDdlEdit: mssql IDENTITY does not accept a CYCLE option — drop `
        + `'cycle' from the IdentitySpec or switch to a sequence-backed default`,
    );
  }
  if (startSet && incrSet) return `IDENTITY(${identity.start}, ${identity.increment})`;
  return 'IDENTITY';
}

function formatPgsql(identity: IdentitySpec): string {
  if (identity.mode === 'by-default-on-null') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: pgsql GENERATED AS IDENTITY does not support `
        + `mode 'by-default-on-null' — that's an Oracle-only generation mode`,
    );
  }
  const mode = identity.mode === 'always' ? 'ALWAYS' : 'BY DEFAULT';
  const opts = formatSequenceOptions(identity, 'NO CYCLE');
  return `GENERATED ${mode} AS IDENTITY${opts}`;
}

function formatMysql(): string {
  return 'AUTO_INCREMENT';
}

function formatOracle(identity: IdentitySpec): string {
  let mode = 'BY DEFAULT';
  if (identity.mode === 'always') mode = 'ALWAYS';
  if (identity.mode === 'by-default-on-null') mode = 'BY DEFAULT ON NULL';
  const opts = formatSequenceOptions(identity, 'NOCYCLE');
  return `GENERATED ${mode} AS IDENTITY${opts}`;
}

function formatSqlite(identity: IdentitySpec): string {
  if (identity.mode === 'by-default-on-null'
      || identity.mode === 'always'
      || identity.mode === 'by-default') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: sqlite AUTOINCREMENT does not support generation modes `
        + `— drop 'mode' from the IdentitySpec`,
    );
  }
  if (identity.cycle !== undefined
      || identity.start !== undefined
      || identity.increment !== undefined) {
    throw new Error(
      `applyDdlEdit: sqlite AUTOINCREMENT does not accept sequence options `
        + `(start / increment / cycle) — drop them from the IdentitySpec`,
    );
  }
  return 'AUTOINCREMENT';
}

function formatSequenceOptions(identity: IdentitySpec, noCycleKeyword: string): string {
  const parts: string[] = [];
  if (identity.start !== undefined) parts.push(`START WITH ${identity.start}`);
  if (identity.increment !== undefined) parts.push(`INCREMENT BY ${identity.increment}`);
  if (identity.cycle === true) parts.push('CYCLE');
  else if (identity.cycle === false) parts.push(noCycleKeyword);
  if (parts.length === 0) return '';
  return ` (${parts.join(' ')})`;
}
